import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  Checkbox,
  IconButton,
  Collapse,
  Tooltip,
  LinearProgress,
  Button,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import TimerIcon from "@mui/icons-material/Timer";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import GlassCard from "../../../components/common/GlassCard";

const TIER_CONFIG = {
  P0: { label: "Continuity & Clinical Progress", color: "error.main", bg: "rgba(244,67,54,0.08)", },
  P1: { label: "Appointments", color: "warning.main", bg: "rgba(255,152,0,0.08)", },
  P2: { label: "Clinical Maintenance", color: "info.main", bg: "rgba(33,150,243,0.08)", },
  P3: { label: "Resource & Compliance", color: "text.secondary", bg: "rgba(158,158,158,0.06)", },
};

const PRIORITY_COLORS = {
  high: "error.main",
  medium: "warning.main",
  low: "text.secondary",
};

function PreCallSummaryCard({ preCallData, loading, onRefresh, onMarkUsed, onAddressItem }) {
  const [expanded, setExpanded] = useState(true);
  const [addressedItems, setAddressedItems] = useState(
    new Set(preCallData?.addressed_indices || preCallData?.addressed_items || [])
  );
  // Saved checkmarks come with each agenda; reload them when a different agenda arrives
  useEffect(() => {
    setAddressedItems(new Set(preCallData?.addressed_indices || preCallData?.addressed_items || []));
  }, [preCallData?.summary_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [tierExpanded, setTierExpanded] = useState({ P0: true, P1: true, P2: true, P3: false });

  if (loading) {
    return (
      <GlassCard title="Pre-Call Agenda" icon={<AssignmentIcon color="primary" />} isAI>
        <Box sx={{ p: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Building agenda pyramid…
          </Typography>
          <LinearProgress sx={{ borderRadius: 1 }} />
        </Box>
      </GlassCard>
    );
  }

  if (!preCallData || preCallData.status === "error") {
    return (
      <GlassCard title="Pre-Call Agenda" icon={<AssignmentIcon color="primary" />} isAI>
        <Typography variant="body2" color="text.secondary">
          No agenda available.
        </Typography>
        {onRefresh && (
          <Button size="small" startIcon={<RefreshIcon />} onClick={onRefresh} sx={{ mt: 1 }}>
            Generate
          </Button>
        )}
      </GlassCard>
    );
  }

  const { tiers, agenda_items = [], estimated_duration_min = 15 } = preCallData;

  // Group items by tier for structured rendering
  const tierGroups = tiers
    ? { P0: tiers.P0 || [], P1: tiers.P1 || [], P2: tiers.P2 || [], P3: tiers.P3 || [] }
    : { P0: [], P1: [], P2: [], P3: [] };

  const hasTiers = tiers && (tierGroups.P0.length || tierGroups.P1.length || tierGroups.P2.length || tierGroups.P3.length);

  // Build a flat list for checkbox indexing
  let flatItems = [];
  if (hasTiers) {
    ["P0", "P1", "P2", "P3"].forEach((tier) => {
      tierGroups[tier].forEach((item) => flatItems.push({ ...item, tier }));
    });
  } else {
    flatItems = agenda_items;
  }

  const totalItems = flatItems.length;
  const completedItems = addressedItems.size;
  const progress = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  const handleToggle = (index) => {
    const next = new Set(addressedItems);
    if (next.has(index)) { next.delete(index); } else { next.add(index); }
    setAddressedItems(next);
    if (onAddressItem) onAddressItem([...next]);
  };

  const toggleTier = (tier) => {
    setTierExpanded((prev) => ({ ...prev, [tier]: !prev[tier] }));
  };

  // ── Single agenda item row ──
  const renderItem = (item, globalIndex) => {
    const isChecked = addressedItems.has(globalIndex);
    const priority = item.priority || (item.tier === "P0" ? "high" : item.tier === "P1" ? "high" : item.tier === "P2" ? "medium" : "low");
    const title = item.title || item.topic || item.talking_point || "";
    const detail = item.detail || item.talking_point || "";
    const source = item.source_label || item.context || "";

    return (
      <Box
        key={globalIndex}
        sx={{
          display: "flex", alignItems: "flex-start", gap: 0.5, mb: 0.75, p: 1,
          borderRadius: 1.5, border: "1px solid",
          borderColor: isChecked ? "success.light" : "rgba(138,43,226,0.15)",
          bgcolor: isChecked ? "rgba(76,175,80,0.05)" : "transparent",
          opacity: isChecked ? 0.6 : 1, transition: "all 0.2s",
        }}
      >
        <Checkbox size="small" inputProps={{ "aria-label": "Mark agenda item done" }} checked={isChecked} onChange={() => handleToggle(globalIndex)} sx={{ p: 0.25, mt: 0.25 }} />
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <FiberManualRecordIcon sx={{ fontSize: 8, color: PRIORITY_COLORS[priority] || "text.secondary", flexShrink: 0 }} />
            <Typography variant="body2" fontWeight={500} sx={{ textDecoration: isChecked ? "line-through" : "none", lineHeight: 1.3 }}>
              {title}
            </Typography>
          </Box>
          {detail && detail !== title && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5, display: "block", lineHeight: 1.3 }}>
              {detail}
            </Typography>
          )}
          {item.why && (
            <Typography variant="caption" sx={{ ml: 1.5, display: "block", mt: 0.25, lineHeight: 1.35 }}>
              <b>Why:</b> {item.why}
            </Typography>
          )}
          {item.ask && (
            <Typography variant="caption" sx={{ ml: 1.5, display: "block", mt: 0.25, lineHeight: 1.35, fontStyle: "italic" }}>
              <b style={{ fontStyle: "normal" }}>Ask:</b> “{item.ask}”
            </Typography>
          )}
          {(source || item.ai_added || item.rule_tier) && (
            <Box sx={{ ml: 1.5, mt: 0.25, display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
              {item.ai_added && (
                <Chip label="AI found" size="small" color="secondary" variant="outlined" sx={{ height: 16, fontSize: "0.6rem" }} />
              )}
              {item.rule_tier && (
                <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "text.secondary" }}>
                  moved from {item.rule_tier}
                </Typography>
              )}
              {source && (
                <Typography variant="caption" sx={{ color: "primary.light", fontSize: "0.65rem", opacity: 0.7 }}>
                  ↳ {source}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Track global index across tiers
  let globalIdx = 0;

  return (
    <GlassCard title="Pre-Call Agenda" icon={<AssignmentIcon color="primary" />} isAI>
      {/* Duration + controls */}
      <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 1.5 }}>
        <TimerIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        <Typography variant="caption" color="text.secondary">
          Est. {estimated_duration_min} min • {totalItems} items
        </Typography>
        <Box flex={1} />
        {onRefresh && (
          <Tooltip title="Regenerate agenda">
            <IconButton size="small" aria-label="Regenerate agenda" onClick={onRefresh}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
        <IconButton size="small" aria-label={expanded ? "Collapse" : "Expand"} onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* AI briefing */}
      {preCallData.ai?.status === "ok" && (preCallData.ai.focus || preCallData.ai.opening) && (
        <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: "rgba(138,43,226,0.06)", border: "1px solid rgba(138,43,226,0.18)" }}>
          {preCallData.ai.focus && (
            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
              <b>Today's focus:</b> {preCallData.ai.focus}
            </Typography>
          )}
          {preCallData.ai.opening && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}>
              Opening: “{preCallData.ai.opening}”
            </Typography>
          )}
        </Box>
      )}
      {preCallData.ai?.status === "fallback" && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {preCallData.ai.message || "AI briefing unavailable; showing the rule-based agenda."}
        </Typography>
      )}

      {/* Progress bar */}
      {totalItems > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary">Agenda Progress</Typography>
            <Typography variant="caption" color="text.secondary">{completedItems}/{totalItems}</Typography>
          </Box>
          <LinearProgress
            variant="determinate" value={progress}
            sx={{
              height: 6, borderRadius: 3, bgcolor: "rgba(138,43,226,0.1)",
              "& .MuiLinearProgress-bar": { bgcolor: progress === 100 ? "success.main" : "primary.main", borderRadius: 3 },
            }}
          />
        </Box>
      )}

      <Collapse in={expanded}>
        {hasTiers ? (
          ["P0", "P1", "P2", "P3"].map((tier) => {
            const tierItems = tierGroups[tier];
            if (tierItems.length === 0) return null;
            const config = TIER_CONFIG[tier];
            const startIdx = globalIdx;
            globalIdx += tierItems.length;

            return (
              <Box key={tier} sx={{ mb: 1.5 }}>
                {/* Tier header */}
                <Box
                  display="flex" alignItems="center" gap={0.5}
                  sx={{ mb: 0.75, p: 0.75, borderRadius: 1, bgcolor: config.bg, cursor: "pointer", userSelect: "none" }}
                  onClick={() => toggleTier(tier)}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ color: config.color, letterSpacing: 0.5 }}>
                    {tier}
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ color: config.color }}>
                    — {config.label}
                  </Typography>
                  <Box flex={1} />
                  <Chip label={tierItems.length} size="small" sx={{ height: 18, fontSize: 10, bgcolor: `${config.color}20`, color: config.color }} />
                  {tierExpanded[tier] ? <ExpandLessIcon sx={{ fontSize: 16, color: config.color }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: config.color }} />}
                </Box>
                <Collapse in={tierExpanded[tier]}>
                  {tierItems.map((item, i) => renderItem(item, startIdx + i))}
                </Collapse>
              </Box>
            );
          })
        ) : (
          agenda_items.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: "block" }}>AGENDA</Typography>
              {agenda_items.map((item, i) => renderItem(item, i))}
            </Box>
          )
        )}
      </Collapse>

      {progress === 100 && totalItems > 0 && (
        <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 1 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "success.main" }} />
          <Typography variant="caption" color="success.main" fontWeight={600}>All agenda items addressed</Typography>
        </Box>
      )}
    </GlassCard>
  );
}

PreCallSummaryCard.propTypes = {
  preCallData: PropTypes.object,
  loading: PropTypes.bool,
  onRefresh: PropTypes.func,
  onMarkUsed: PropTypes.func,
  onAddressItem: PropTypes.func,
};

PreCallSummaryCard.defaultProps = {
  preCallData: null,
  loading: false,
};

export default PreCallSummaryCard;
